import React, { useState, useRef, useEffect } from 'react';
import { Send, Sparkles, FileText, ListChecks, Reply, Loader2, Mail, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Clock, User2, CheckSquare, StickyNote } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { apiPost } from '../../utils/api';
import toast from 'react-hot-toast';
import CreateTaskModal from '../tasks/CreateTaskModal';
import AddToNotesModal from '../notes/AddToNotesModal';

const QUICK_ACTIONS = [
    { mode: 'summarize', label: 'Summarize', prompt: 'Summarize this email', icon: FileText },
    { mode: 'extract_action_items', label: 'Action Items', prompt: 'Extract action items', icon: ListChecks },
    { mode: 'draft_reply', label: 'Draft Reply', prompt: 'Draft a reply', icon: Reply },
];

export default function EmailAIChat({
    selectedEmails,
    fullEmails = {},
    onAnalyze,
    onSearchSender,
    loading,
}) {
    const { theme } = useTheme();
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [showEmailBody, setShowEmailBody] = useState(true);
    const [currentEmailIndex, setCurrentEmailIndex] = useState(0);
    const [showCreateTask, setShowCreateTask] = useState(false);
    const [taskPrefill, setTaskPrefill] = useState({});
    const [showSaveToNotes, setShowSaveToNotes] = useState(false);
    const [noteContent, setNoteContent] = useState('');
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);


    // Reset chat and carousel when selected emails change
    useEffect(() => {
        setMessages([]);
        setShowEmailBody(true);
        setCurrentEmailIndex(0);
    }, [selectedEmails.map(e => e.id).join(',')]);

    // Keep carousel index in bounds when selection shrinks
    useEffect(() => {
        if (currentEmailIndex >= selectedEmails.length && selectedEmails.length > 0) {
            setCurrentEmailIndex(selectedEmails.length - 1);
        }
    }, [selectedEmails.length, currentEmailIndex]);

    const currentEmail = selectedEmails[currentEmailIndex] || null;
    const currentFullEmail = currentEmail ? fullEmails[currentEmail.id] : null;
    const isCurrentLoading = currentEmail && !currentFullEmail;

    async function handleSend(mode = 'ask_question', question = null) {
        const text = question || input.trim();
        if (!text && mode === 'ask_question') return;
        if (selectedEmails.length === 0) return;

        const action = QUICK_ACTIONS.find(a => a.mode === mode);
        const userMsg = mode === 'ask_question' ? text : action?.prompt || mode;
        setMessages(prev => [...prev, { role: 'user', content: userMsg, type: mode === 'ask_question' ? 'message' : 'action' }]);
        if (mode === 'ask_question') setInput('');

        try {
            const result = await onAnalyze({
                email_ids: selectedEmails.map(e => e.id),
                analysis_mode: mode,
                user_question: mode !== 'ask_question' ? undefined : text,
                chat_history: messages.map(m => ({
                    role: m.role,
                    sender: m.role,
                    content: m.content,
                })),
            });

            if (result?.content) {
                setMessages(prev => [...prev, {
                    role: 'assistant',
                    content: result.content,
                    mode: result.mode,
                    model: result.model,
                    tasks: result.tasks || [],
                }]);
            }
        } catch {
            setMessages(prev => [...prev, {
                role: 'assistant',
                content: 'Sorry, something went wrong analyzing the email. Please try again.',
            }]);
        }
    }

    function handleKeyDown(e) {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    }

    const noEmailsSelected = selectedEmails.length === 0;
    const senderName = currentEmail?.from?.emailAddress?.name || currentEmail?.from?.emailAddress?.address || 'Unknown';
    const senderAddress = currentEmail?.from?.emailAddress?.address;

    return (<>
        <div className="flex flex-col h-full">
            {/* Header — email info with carousel nav */}
            {selectedEmails.length > 0 && currentEmail && (
                <div className={`px-4 py-3 border-b ${
                    theme === 'dark' ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-gray-50'
                }`}>
                    {/* Carousel navigation for multiple emails */}
                    {selectedEmails.length > 1 && (
                        <div className={`flex items-center gap-1 mb-2 ${
                            theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                        }`}>
                            <button
                                onClick={() => setCurrentEmailIndex(i => Math.max(0, i - 1))}
                                disabled={currentEmailIndex === 0}
                                className={`p-0.5 rounded transition-colors disabled:opacity-30 ${
                                    theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-200'
                                }`}
                            >
                                <ChevronLeft size={16} />
                            </button>
                            <span className="text-sm font-rasa font-medium">
                                {currentEmailIndex + 1} of {selectedEmails.length} emails
                            </span>
                            <button
                                onClick={() => setCurrentEmailIndex(i => Math.min(selectedEmails.length - 1, i + 1))}
                                disabled={currentEmailIndex === selectedEmails.length - 1}
                                className={`p-0.5 rounded transition-colors disabled:opacity-30 ${
                                    theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-200'
                                }`}
                            >
                                <ChevronRight size={16} />
                            </button>
                        </div>
                    )}

                    {/* Current email subject */}
                    <div className={`font-unna font-medium text-sm ${theme === 'dark' ? 'text-gray-100' : 'text-gray-900'}`}>
                        {currentEmail.subject || '(no subject)'}
                    </div>

                    {/* Sender (clickable) + date */}
                    <div className={`flex items-center gap-3 mt-1 text-sm font-rasa ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                        <button
                            onClick={() => senderAddress && onSearchSender?.(senderAddress)}
                            className={`flex items-center gap-1 transition-colors ${
                                senderAddress
                                    ? theme === 'dark' ? 'hover:text-blue-400' : 'hover:text-blue-600'
                                    : ''
                            }`}
                            title={senderAddress ? `View all emails from ${senderAddress}` : undefined}
                            disabled={!senderAddress}
                        >
                            <User2 size={12} />
                            {senderName}
                        </button>
                        {currentEmail.receivedDateTime && (
                            <span className="flex items-center gap-1">
                                <Clock size={12} />
                                {new Date(currentEmail.receivedDateTime).toLocaleString()}
                            </span>
                        )}
                    </div>
                </div>
            )}

            {/* Messages / content area */}
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
                {/* Empty state — no email selected */}
                {noEmailsSelected && messages.length === 0 && (
                    <div className={`flex flex-col items-center justify-center h-full text-center ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                        <Sparkles size={32} className="mb-3 opacity-50" />
                        <p className="font-unna font-medium mb-1">JPRO Email Assistant</p>
                        <p className="text-base font-rasa">Select an email from the sidebar to get started.<br />You can summarize, ask questions, or draft replies.</p>
                    </div>
                )}

                {/* Email body display */}
                {selectedEmails.length > 0 && showEmailBody && (
                    <div className={`rounded-lg border overflow-hidden ${
                        theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
                    }`}>
                        {isCurrentLoading ? (
                            <div className={`flex items-center gap-2 p-4 text-sm ${
                                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                            }`}>
                                <Loader2 size={14} className="animate-spin text-blue-500" />
                                Loading email...
                            </div>
                        ) : currentFullEmail?.body?.content ? (
                            <iframe
                                key={currentEmail.id}
                                srcDoc={currentFullEmail.body.content}
                                sandbox="allow-same-origin"
                                title="Email content"
                                className="w-full border-0 bg-white"
                                style={{ minHeight: '200px' }}
                                onLoad={(e) => {
                                    try {
                                        const doc = e.target.contentDocument;
                                        if (doc?.body) {
                                            const height = Math.min(doc.body.scrollHeight + 20, window.innerHeight * 0.55);
                                            e.target.style.height = height + 'px';
                                        }
                                    } catch {
                                        // Sandbox restriction — keep default height
                                    }
                                }}
                            />
                        ) : null}
                    </div>
                )}

                {/* Toggle email body when chatting */}
                {selectedEmails.length > 0 && messages.length > 0 && (
                    <button
                        onClick={() => setShowEmailBody(!showEmailBody)}
                        className={`flex items-center gap-1 text-xs mx-auto py-1 px-2 rounded transition-colors ${
                            theme === 'dark'
                                ? 'text-gray-400 hover:text-gray-300 hover:bg-gray-800'
                                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                        }`}
                    >
                        {showEmailBody ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                        {showEmailBody ? 'Hide email' : 'Show email'}
                    </button>
                )}

                {/* Quick actions when no messages yet */}
                {selectedEmails.length > 0 && messages.length === 0 && !isCurrentLoading && (
                    <div className="flex flex-wrap gap-2 justify-center pt-2">
                        {QUICK_ACTIONS.map(({ mode, label, icon: Icon }) => (
                            <button
                                key={mode}
                                onClick={() => handleSend(mode, label)}
                                disabled={loading}
                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-rasa transition-colors ${
                                    theme === 'dark'
                                        ? 'bg-gray-700 hover:bg-gray-600 text-gray-200'
                                        : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                                }`}
                            >
                                <Icon size={14} />
                                {label}
                            </button>
                        ))}
                    </div>
                )}

                {/* Chat messages */}
                {messages.map((msg, i) => (
                    msg.type === 'action' ? (
                        <div key={i} className="flex justify-center">
                            <span className={`inline-flex items-center gap-1.5 text-xs px-3 py-1 rounded-full ${
                                theme === 'dark' ? 'bg-gray-700/60 text-gray-400' : 'bg-gray-100 text-gray-500'
                            }`}>
                                <Sparkles size={11} />
                                {msg.content}
                            </span>
                        </div>
                    ) : (
                        <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                            <div className={`max-w-full sm:max-w-[90%] md:max-w-[85%] rounded-xl px-3 sm:px-4 py-2.5 text-base overflow-hidden ${
                                msg.role === 'user'
                                    ? 'bg-blue-600 text-white'
                                    : theme === 'dark'
                                        ? 'bg-gray-700 text-gray-100'
                                        : 'bg-gray-100 text-gray-800'
                            }`}>
                                {msg.role === 'assistant' ? (
                                    <>
                                        <div className="prose prose-base max-w-none dark:prose-invert font-rasa prose-headings:font-unna prose-headings:mt-2 prose-headings:mb-0.5 prose-headings:text-[0.85em] prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-li:my-0.5 prose-hr:my-2 prose-table:my-2 prose-pre:my-1.5 overflow-x-auto break-words">
                                            <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown>
                                        </div>

                                        {/* AI-extracted tasks — structured with title, description, priority */}
                                        {msg.tasks?.length > 0 && (
                                            <div className={`mt-2 pt-2 border-t space-y-1.5 ${theme === 'dark' ? 'border-gray-600' : 'border-gray-200'}`}>
                                                <span className={`text-xs font-rasa font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                                                    {msg.tasks.length} task{msg.tasks.length !== 1 ? 's' : ''} identified
                                                </span>
                                                {msg.tasks.map((task, idx) => {
                                                    const prioColor = { urgent: 'bg-red-500', high: 'bg-orange-500', medium: 'bg-orange-400', low: 'bg-green-400' }[task.priority] || 'bg-gray-400';
                                                    return (
                                                        <div key={idx} className={`flex items-start gap-2.5 p-2 rounded-lg ${theme === 'dark' ? 'bg-gray-600/40' : 'bg-white/60'}`}>
                                                            <div className={`w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 ${prioColor}`} title={task.priority} />
                                                            <div className="flex-1 min-w-0">
                                                                <p className={`text-sm font-rasa font-medium leading-snug ${theme === 'dark' ? 'text-gray-200' : 'text-gray-800'}`}>
                                                                    {task.title}
                                                                </p>
                                                                {task.description && task.description !== task.title && (
                                                                    <p className={`text-xs font-rasa mt-0.5 leading-snug ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                                                                        {task.description.substring(0, 120)}
                                                                    </p>
                                                                )}
                                                                {task.category && (
                                                                    <span className={`inline-block text-[9px] font-rasa mt-1 px-1.5 py-0.5 rounded ${theme === 'dark' ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                                                                        {task.category}
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <button
                                                                onClick={() => {
                                                                    setTaskPrefill({
                                                                        title: task.title,
                                                                        description: task.description,
                                                                        priority: task.priority,
                                                                        category: task.category,
                                                                        source_type: 'email_ai',
                                                                        source_label: 'Email AI',
                                                                    });
                                                                    setShowCreateTask(true);
                                                                }}
                                                                className={`flex-shrink-0 flex items-center gap-1 px-2 py-1 rounded text-xs font-rasa transition-colors ${
                                                                    theme === 'dark' ? 'bg-blue-600/30 hover:bg-blue-600/50 text-blue-300' : 'bg-blue-50 hover:bg-blue-100 text-blue-600'
                                                                }`}
                                                            >
                                                                <CheckSquare size={10} />
                                                                Add
                                                            </button>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}

                                        {/* Action buttons */}
                                        <div className={`flex items-center gap-1.5 mt-2 pt-2 border-t ${
                                            theme === 'dark' ? 'border-gray-600' : 'border-gray-200'
                                        }`}>
                                            {/* Manual task creation (if AI didn't find any) */}
                                            {!msg.tasks?.length && (
                                                <button
                                                    onClick={() => {
                                                        setTaskPrefill({ description: msg.content, source_type: 'email_ai', source_label: 'Email AI' });
                                                        setShowCreateTask(true);
                                                    }}
                                                    className={`flex items-center gap-1 px-2 py-1 rounded text-sm font-rasa transition-colors ${
                                                        theme === 'dark' ? 'hover:bg-gray-600 text-gray-400' : 'hover:bg-gray-200 text-gray-500'
                                                    }`}
                                                    title="Create task manually"
                                                >
                                                    <CheckSquare size={12} />
                                                    Task
                                                </button>
                                            )}
                                            <button
                                                onClick={() => {
                                                    setNoteContent(msg.content);
                                                    setShowSaveToNotes(true);
                                                }}
                                                className={`flex items-center gap-1 px-2 py-1 rounded text-sm font-rasa transition-colors ${
                                                    theme === 'dark' ? 'hover:bg-gray-600 text-gray-400' : 'hover:bg-gray-200 text-gray-500'
                                                }`}
                                                title="Save to Notes"
                                            >
                                                <StickyNote size={12} />
                                                Note
                                            </button>
                                        </div>
                                    </>
                                ) : (
                                    <p className="break-words font-rasa">{msg.content}</p>
                                )}
                            </div>
                        </div>
                    )
                ))}

                {loading && (
                    <div className="flex justify-start">
                        <div className={`rounded-xl px-4 py-3 ${
                            theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100'
                        }`}>
                            <Loader2 size={16} className="animate-spin text-blue-500" />
                        </div>
                    </div>
                )}

                <div ref={messagesEndRef} />
            </div>

            {/* Quick actions when messages exist */}
            {selectedEmails.length > 0 && messages.length > 0 && (
                <div className={`px-4 py-2 flex gap-2 border-t ${
                    theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
                }`}>
                    {QUICK_ACTIONS.map(({ mode, label, icon: Icon }) => (
                        <button
                            key={mode}
                            onClick={() => handleSend(mode, label)}
                            disabled={loading || noEmailsSelected}
                            className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-rasa transition-colors ${
                                theme === 'dark'
                                    ? 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                                    : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
                            } disabled:opacity-50`}
                        >
                            <Icon size={12} />
                            {label}
                        </button>
                    ))}
                </div>
            )}

            {/* Input */}
            <div className={`px-4 py-3 border-t ${
                theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
            }`}>
                <div className={`flex items-end gap-2 rounded-xl border px-3 py-2 ${
                    theme === 'dark' ? 'border-gray-600 bg-gray-800' : 'border-gray-300 bg-white'
                }`}>
                    <textarea
                        ref={inputRef}
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder={
                            noEmailsSelected
                                ? 'Select an email first...'
                                : selectedEmails.length > 1
                                    ? `Ask about these ${selectedEmails.length} emails...`
                                    : 'Ask about this email...'
                        }
                        disabled={noEmailsSelected || loading}
                        rows={1}
                        className={`flex-1 resize-none bg-transparent border-none outline-none text-base font-rasa ${
                            theme === 'dark' ? 'text-white placeholder-gray-500' : 'text-gray-900 placeholder-gray-400'
                        } disabled:opacity-50`}
                    />
                    <button
                        onClick={() => handleSend()}
                        disabled={!input.trim() || noEmailsSelected || loading}
                        className="p-1.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                        <Send size={16} />
                    </button>
                </div>
            </div>
        </div>

        {showCreateTask && (
            <CreateTaskModal
                onClose={() => setShowCreateTask(false)}
                onSubmit={async (taskData) => {
                    try {
                        const response = await apiPost('/tasks', taskData);
                        if (response.ok) toast.success('Task created');
                    } catch { /* silent */ }
                    setShowCreateTask(false);
                }}
                theme={theme}
                prefill={taskPrefill}
            />
        )}

        {showSaveToNotes && (
            <AddToNotesModal
                content={noteContent}
                sourceType="email_ai"
                onClose={() => setShowSaveToNotes(false)}
                theme={theme}
            />
        )}
    </>);
}
